import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';

// ============================================================================
// API payload shapes for the Communication Dashboard. Property names are
// camelCase to match ASP.NET Core's default JSON serialization of the
// CommDashboard* DTOs. Date fields arrive as ISO strings (UTC, no offset).
// ============================================================================

export type Period = 'week' | 'month';

export interface CommDashboardSummaryDto {
	emailRequests: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounces: number;
	emailSpamReports: number;
	emailUnsubscribed: number;
	emailDeliveredPct: number;
	emailOpenedPct: number;
	emailClickedPct: number;
	emailBouncesPct: number;
	emailSpamPct: number;
	emailUnsubscribedPct: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
	smsDeliveredPct: number;
	smsUndeliveredPct: number;
	smsFailedPct: number;
}

export interface CommActivityPointDto {
	activityDate: string;
	emailSent: number;
	emailDelivered: number;
	emailOpened: number;
	emailClicked: number;
	emailBounced: number;
	smsSent: number;
	smsDelivered: number;
	smsUndelivered: number;
	smsFailed: number;
}

export interface CommEmployerDto {
	employerID: string;
	employerName: string;
	deliveryTypeID: number;
	commPreference: string;
	dripStartDate: string | null;
	censusLoaded: number;
	campaignsSent: number;
	campaignsTotal: number;
	lastCampaign: string | null;
	nextCampaign: string | null;
	emailDeliveredPct: number | null;
	smsDeliveredPct: number | null;
	hasMissingFields: boolean;
	hasIssues: boolean;
	stage: 'build' | 'staged' | 'active';
}

export interface CommCampaignDto {
	campaignID: string;
	campaignName: string;
	type: 'Single' | 'Drip';
	isScheduled: boolean;
	scheduleStatus: 'sent' | 'scheduled' | 'overdue' | 'paused';
	sendDate: string | null;
	emailSent: number;
	emailDeliveredPct: number | null;
	openedPct: number | null;
	clickedPct: number | null;
	bouncedPct: number | null;
	droppedPct: number | null;
	spamPct: number | null;
	smsSent: number;
	smsDeliveredPct: number | null;
	smsFailPct: number | null;
	alertHighBounce: boolean;
	alertHighSpam: boolean;
	alertHighDrop: boolean;
	alertHighDeferral: boolean;
	alertLowDelivery: boolean;
	alertHighSmsFailure: boolean;
	alertLowSmsDelivery: boolean;
	alertEmptyCensus: boolean;
}

export interface CommAlertDto {
	type: 'error' | 'warning' | 'info';
	category: string;
	message: string;
}

export interface CommEmployerDetailDto {
	employer: CommEmployerDto;
	allTime: CommDashboardSummaryDto;
	campaigns: CommCampaignDto[];
	alerts: CommAlertDto[];
}

// One row of dbo.omCommAlertThreshold. This is the SINGLE source of truth for every
// metric color shown on the dashboard — the components evaluate each displayed rate
// against the matching (metricCode, channel) row instead of hardcoding any numbers.
export type ThresholdSeverity = 'warning' | 'error' | null;

export interface CommAlertThresholdDto {
	metricCode: string;   // BounceRate | SpamRate | DropRate | DeferralRate | DeliveryRate | SMSFailureRate
	channel: string;      // Email | SMS
	comparator: string;   // '>' or '<'
	thresholdPct: number;
	severity: string;     // Warning | Error
	isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class CommDashboardService {

	// Active thresholds, cached for the service lifetime so the synchronous coloring helpers
	// below can run during template change-detection. Populated by getThresholds().
	private thresholds: CommAlertThresholdDto[] = [];

	constructor(private api: ApiService) {}

	getSummary(period: Period, employerId?: string): Observable<CommDashboardSummaryDto> {
		return this.api.get<CommDashboardSummaryDto>(`commdashboard/summary?${this.qs(period, employerId)}`);
	}

	getActivity(period: Period, employerId?: string): Observable<CommActivityPointDto[]> {
		return this.api.get<CommActivityPointDto[]>(`commdashboard/activity?${this.qs(period, employerId)}`);
	}

	getEmployers(): Observable<CommEmployerDto[]> {
		return this.api.get<CommEmployerDto[]>(`commdashboard/employers`);
	}

	getEmployerDetail(employerId: string): Observable<CommEmployerDetailDto> {
		return this.api.get<CommEmployerDetailDto>(`commdashboard/employers/${employerId}`);
	}

	// Loads the active alert thresholds and caches them. Include this in the dashboard's
	// initial forkJoin so the cache is warm before the grid/stat values render.
	getThresholds(): Observable<CommAlertThresholdDto[]> {
		return this.api.get<CommAlertThresholdDto[]>(`commdashboard/thresholds`)
			.pipe(tap(list => (this.thresholds = list ?? [])));
	}

	// ── Table-driven metric coloring ────────────────────────────────────
	// Every displayed rate is colored ONLY by comparing it against its matching
	// omCommAlertThreshold row. No thresholds live in the components or templates.

	/**
	 * Returns the severity a value breaches for a given (metricCode, channel), or null when it
	 * is within tolerance / there is no active threshold / the value is missing. The table's PK
	 * is (metricCode, channel), so at most one row matches; its comparator + thresholdPct decide
	 * the breach and its severity decides the color.
	 */
	breachSeverity(value: number | null | undefined, metricCode: string, channel: string): ThresholdSeverity {
		if (value === null || value === undefined) {
			return null;
		}
		const t = this.thresholds.find(x => x.isActive && x.metricCode === metricCode && x.channel === channel);
		if (!t) {
			return null;
		}
		const breached = t.comparator === '>' ? value > t.thresholdPct : value < t.thresholdPct;
		if (!breached) {
			return null;
		}
		return (t.severity || '').toLowerCase() === 'error' ? 'error' : 'warning';
	}

	/** ngClass map for grid cells (employer grid + campaign table). */
	rateCellClass(value: number | null | undefined, metricCode: string, channel: string): { [cls: string]: boolean } {
		const sev = this.breachSeverity(value, metricCode, channel);
		return { 'rate-warn': sev === 'warning', 'rate-error': sev === 'error' };
	}

	/** ngClass map for the summary stat-card values. */
	statValueClass(value: number | null | undefined, metricCode: string, channel: string): { [cls: string]: boolean } {
		const sev = this.breachSeverity(value, metricCode, channel);
		return { 'warn': sev === 'warning', 'error': sev === 'error' };
	}

	private qs(period: Period, employerId?: string): string {
		const params = new URLSearchParams();
		params.set('period', period);
		if (employerId) {
			params.set('employerId', employerId);
		}
		return params.toString();
	}
}
